import { useEffect, useState } from "react";
import { ShieldStar, Broom, Crown } from "@phosphor-icons/react";
import { api } from "../lib/api";

const COLORS = {
  SECURITY: { ring: "text-neon-cyan", bar: "bg-neon-cyan", chip: "border-neon-cyan/30 bg-neon-cyan/10 text-neon-cyan" },
  HOUSEKEEPING: { ring: "text-neon-lime", bar: "bg-neon-lime", chip: "border-neon-lime/30 bg-neon-lime/10 text-neon-lime" },
};

const RANGES = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
];

export default function AttendanceSummary() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("today");

  useEffect(() => {
    const qs = (range === "today" || range === "yesterday") ? `?period=${range}` : `?days=${range}`;
    setData(null);
    api.get(`/api/attendance/summary${qs}`).then(({ data }) => setData(data)).catch(() => {});
  }, [range]);

  const rangeLabel = RANGES.find((r) => r.value === range)?.label || "Today";

  return (
    <div className="space-y-4 font-sans" data-testid="att-summary-screen">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">{rangeLabel}</p>
          <h1 className="font-display text-3xl font-extrabold text-white">Attendance summary</h1>
          <p className="mt-1 text-sm text-white/55">Supervisor-wise present % across security shifts and housekeeping.</p>
        </div>
        <select className="field !w-auto !py-2.5 text-[13px]" value={range} onChange={(e) => setRange(e.target.value)} data-testid="summary-window">
          {RANGES.map((r) => <option key={r.value} value={r.value} className="bg-ink-800">{r.label}</option>)}
        </select>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {(data?.buckets || []).map((b) => {
          const c = b.label.startsWith("Security") ? COLORS.SECURITY : COLORS.HOUSEKEEPING;
          const Icon = b.label.startsWith("Security") ? ShieldStar : Broom;
          return (
            <div key={b.key} className="card p-4" data-testid={`summary-${b.key}`}>
              <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-white/[0.04] ${c.ring}`}><Icon size={20} weight="duotone" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-[15px] font-bold text-white">{b.label}</p>
                  <p className="text-[11px] text-white/55">
                    {b.supervisor ? <><Crown size={11} weight="fill" className="-mt-0.5 mr-1 inline text-neon-amber" />{b.supervisor}</> : <span className="text-white/40">No supervisor assigned</span>}
                    <span className="ml-1">· {b.members.length} member{b.members.length === 1 ? "" : "s"}</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className={`font-display text-2xl font-extrabold ${c.ring}`}>{b.attendancePct}%</p>
                  <p className="text-[10px] uppercase tracking-wider text-white/45">{b.presentDays} / {b.expectedDays}</p>
                </div>
              </div>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <div className={`h-full ${c.bar}`} style={{ width: `${b.attendancePct}%` }} />
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {b.members.map((m) => <span key={m.id} className={`chip ${m.name === b.supervisor ? c.chip : ""}`}>{m.name}{m.name === b.supervisor ? " ★" : ""}</span>)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
